import { getOrderDeliveryStatus } from '@koala/sdk/v4';
import { call, put, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import { deliveryStatusActions as actions, deliveryStatusActionTypes as types } from './actions';
import globalActions from '@/redux/global/actions';
import { createHttpClient } from '@/services/client';
import { getOrigin } from '@/utils';
import * as ErrorReporter from '@/utils/errorReporter';
import { isRetryableError } from '@/utils/networkErrors';

export function* fetchDeliveryStatusByOrderNumberSaga(
  action: ReturnType<typeof actions.fetchDeliveryStatusByOrderNumber>,
) {
  try {
    const origin = getOrigin(window.location.host);
    const client = createHttpClient({ origin });
    const response: SagaReturnType<typeof getOrderDeliveryStatus> = yield call(
      getOrderDeliveryStatus,
      action.order_number!,
      { client },
    );

    // Write menu into redux
    yield put(actions.fetchDeliveryStatusByOrderNumberSuccess(response));
  } catch (error) {
    yield put(actions.fetchDeliveryStatusByOrderNumberFail(error));

    // Transient failures (502, non-JSON gateway pages, network errors) are
    // expected during deploys. Silently report to Sentry — the next 60-second
    // poll will retry. Showing a toast on every cycle alarmed paid diners
    // sitting on the confirmation page (TEAM-14263).
    if (isRetryableError(error)) {
      ErrorReporter.captureException(error, 'warning', {
        eventName: 'DELIVERY_STATUS_TRANSIENT_FAILURE',
      });
      return;
    }

    console.error(error);
    ErrorReporter.captureException(error);
    yield put(
      globalActions.displayErrorToast(
        'There was an error fetching your delivery status. Please refresh the page.',
        true,
      ),
    );
  }
}

export default function* rootSaga() {
  yield takeLatest(
    types.FETCH_DELIVERY_STATUS_BY_ORDER_NUMBER,
    fetchDeliveryStatusByOrderNumberSaga,
  );
}
